// ProactiveReviewModal — piloto de avaliação proativa (Parte 1, rodada 3).
// Componente apresentacional: recebe o booking elegível já resolvido pelo
// pai (GET /public/:slug/reviews/pending) e só chama o endpoint de review já
// existente e testado (POST /public/:slug/reviews com booking_id) — nenhuma
// lógica de negócio nova aqui, nenhuma avaliação sintética é criada.
import React, { useState } from 'react';
import { Box, Typography, Dialog, DialogContent, TextField } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';

function palette(isDark: boolean) {
  return isDark
    ? { bg: '#0f0f0f', surface: '#1a1a1a', surfaceAlt: '#222222', border: '#2e2e2e', tPrimary: '#f1f5f9', tSecondary: '#94a3b8', tMuted: '#64748b' }
    : { bg: '#f7f8fa', surface: '#ffffff', surfaceAlt: '#f5f5f5', border: '#ececec', tPrimary: '#111111', tSecondary: '#666666', tMuted: '#999999' };
}

export interface PendingReviewBooking {
  id: number;
  service_name: string | null;
  professional_name: string | null;
  date: string;
}

export function ProactiveReviewModal({
  open, booking, isDark, accentColor, onSkip, onSubmit, submitting,
}: {
  open: boolean;
  booking: PendingReviewBooking | null;
  isDark: boolean;
  accentColor: string;
  onSkip: () => void;
  onSubmit: (rating: number, comment: string) => void;
  submitting: boolean;
}) {
  const C = palette(isDark);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');

  if (!booking) return null;

  const dateLabel = (() => {
    try {
      const d = new Date(booking.date + 'T12:00:00');
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return booking.date; }
  })();

  return (
    <Dialog open={open} onClose={onSkip} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4, m: 2, bgcolor: C.bg, backgroundImage: 'none' } } }}>
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: C.tPrimary, mb: 0.5, textAlign: 'center' }}>
          Como foi seu atendimento?
        </Typography>
        <Typography sx={{ fontSize: 13, color: C.tSecondary, textAlign: 'center', mb: 2 }}>
          {[booking.service_name, booking.professional_name].filter(Boolean).join(' · ')}
          {booking.date ? ` — ${dateLabel}` : ''}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 2 }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Box key={s} role="button" aria-label={`${s} estrelas`} onClick={() => setRating(s)}
              sx={{ cursor: 'pointer', p: 0.5, display: 'flex' }}>
              {s <= rating ? <Star sx={{ fontSize: 32, color: '#f59e0b' }} /> : <StarBorder sx={{ fontSize: 32, color: C.tMuted }} />}
            </Box>
          ))}
        </Box>

        <TextField
          fullWidth multiline rows={3} size="small"
          placeholder="Conte como foi sua experiência (opcional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: C.surfaceAlt, color: C.tPrimary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button
            disabled={submitting}
            onClick={() => onSubmit(rating, comment)}
            style={{
              width: '100%', minHeight: 46, borderRadius: 14, border: 'none', cursor: submitting ? 'default' : 'pointer',
              background: accentColor, color: '#fff', fontWeight: 800, fontSize: 14.5, opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Enviando...' : 'Enviar avaliação'}
          </button>
          <button
            disabled={submitting}
            onClick={onSkip}
            style={{
              width: '100%', minHeight: 44, borderRadius: 14, border: 'none', cursor: 'pointer',
              background: 'transparent', color: C.tMuted, fontWeight: 700, fontSize: 13.5,
            }}
          >
            Agora não
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
